import React, { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Timeline } from "@/components/Timeline";
import { ReceiptModal } from "@/components/ReceiptModal";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CUSTOMER } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { MapPin, FileText, Eye, PackageSearch } from "lucide-react";
import { toast } from "sonner";

export default function CustomerPortal() {
  const { user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [detail, setDetail] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/my/shipments");
        setShipments(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <h1 className="text-2xl font-black tracking-tight sm:text-3xl">My Shipments</h1>
        <p className="text-sm text-slate-400">Shipments linked to {user?.email}</p>

        {!loading && shipments.length === 0 && (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/50 p-12 text-center">
            <PackageSearch className="h-10 w-10 text-slate-500" />
            <p className="text-lg font-bold">No shipments yet</p>
            <p className="text-sm text-slate-400">When Go Deliver links a shipment to your email, it will appear here.</p>
          </div>
        )}

        <div data-testid={CUSTOMER.grid} className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {shipments.map((s) => (
            <div
              key={s.shipment_id}
              data-testid={`${CUSTOMER.card}${s.tracking_number}`}
              className="flex flex-col rounded-2xl border border-white/10 bg-slate-900/60 p-5 transition-colors hover:border-orange-500/40"
            >
              <div className="flex items-start justify-between">
                <p className="font-mono text-sm font-bold text-white">{s.tracking_number}</p>
                <StatusBadge status={s.status} />
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm text-slate-300">
                <MapPin className="h-4 w-4 text-orange-400" /> {s.origin} → {s.destination}
              </div>
              <p className="mt-1 text-xs text-slate-500">{s.package_type} · {s.weight} kg · {s.service_tier}</p>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1 border-white/15 bg-transparent text-slate-200 hover:bg-white/10" onClick={() => setDetail(s)}>
                  <Eye className="mr-1 h-4 w-4" /> Timeline
                </Button>
                <Button size="sm" variant="outline" className="flex-1 border-white/15 bg-transparent text-slate-200 hover:bg-white/10" onClick={() => setReceipt(s)}>
                  <FileText className="mr-1 h-4 w-4" /> Receipt
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={Boolean(detail)} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-mono">{detail?.tracking_number}</DialogTitle>
          </DialogHeader>
          {detail && <Timeline status={detail.status} history={detail.status_history} />}
        </DialogContent>
      </Dialog>

      <ReceiptModal open={Boolean(receipt)} onOpenChange={(v) => !v && setReceipt(null)} shipment={receipt} />
    </div>
  );
}
